<template>
  <VApp>
    <!-- Status Bar Configuration -->
    <VAppBar
      v-if="!hideHeader"
      elevation="0"
      color="white"
      :class="{ 'safe-area-top': isNative }"
    >
      <VBtn
        v-if="canGoBack"
        icon
        @click="handleBack"
      >
        <VIcon>mdi-arrow-left</VIcon>
      </VBtn>

      <VAppBarTitle>{{ pageTitle }}</VAppBarTitle>

      <VSpacer />

      <slot name="header-actions" />
    </VAppBar>

    <!-- Main Content with Pull to Refresh -->
    <VMain :class="{ 'safe-area-bottom': isNative && showBottomNav }">
      <slot />
    </VMain>

    <!-- Bottom Navigation -->
    <VBottomNavigation
      v-if="showBottomNav"
      v-model="activeTab"
      grow
      :class="{ 'safe-area-bottom': isNative }"
    >
      <VBtn value="home" @click="navigateTo('/mobile-home')">
        <VIcon>mdi-home</VIcon>
        <span>Home</span>
      </VBtn>

      <VBtn value="search" @click="navigateTo('/restaurants')">
        <VIcon>mdi-magnify</VIcon>
        <span>Search</span>
      </VBtn>

      <VBtn value="orders" @click="navigateTo('/my/orders')">
        <VIcon>mdi-receipt</VIcon>
        <span>Orders</span>
        <VBadge
          v-if="activeOrdersCount > 0"
          :content="activeOrdersCount"
          color="error"
          inline
        />
      </VBtn>

      <VBtn value="account" @click="navigateTo('/my/profile')">
        <VIcon>mdi-account</VIcon>
        <span>Account</span>
      </VBtn>
    </VBottomNavigation>
  </VApp>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { usePlatform } from '@/composables/usePlatform'

const router = useRouter()
const route = useRoute()
const { isNative } = usePlatform()

// Page state
const activeTab = ref('home')
const pageTitle = computed(() => route.meta.title || 'Starvly')
const hideHeader = computed(() => route.meta.hideHeader || false)
const showBottomNav = computed(() => !route.meta.hideBottomNav)
const activeOrdersCount = ref(0)

// Navigation
const canGoBack = computed(() => {
  return window.history.length > 1 && route.path !== '/'
})

const handleBack = () => {
  router.back()
}

// Update active tab based on current route
watch(() => route.path, (newPath) => {
  if (newPath.startsWith('/restaurants')) {
    activeTab.value = 'search'
  } else if (newPath.startsWith('/my/orders')) {
    activeTab.value = 'orders'
  } else if (newPath.startsWith('/my')) {
    activeTab.value = 'account'
  } else if (newPath === '/mobile-home' || newPath === '/') {
    activeTab.value = 'home'
  } else {
    activeTab.value = 'home'
  }
}, { immediate: true })

// Setup native features
onMounted(async () => {
  if (isNative) {
    // Configure status bar
    try {
      const { StatusBar, Style } = await import('@capacitor/status-bar')
      await StatusBar.setStyle({ style: Style.Light })
      await StatusBar.setBackgroundColor({ color: '#ffffff' })
    } catch (error) {
      console.warn('StatusBar not available:', error)
    }

    // Setup splash screen
    try {
      const { SplashScreen } = await import('@capacitor/splash-screen')
      await SplashScreen.hide()
    } catch (error) {
      console.warn('SplashScreen not available:', error)
    }

    // Initialize push notifications
    const userStore = useUserStore()
    if (userStore.isAuthenticated) {
      try {
        const { usePushNotifications } = await import('@/composables/usePushNotifications')
        const { register, deviceToken, sendTokenToBackend } = usePushNotifications()

        const registered = await register()
        if (registered && deviceToken.value) {
          await sendTokenToBackend(deviceToken.value)
        }
      } catch (error) {
        console.warn('Push notifications not available:', error)
      }
    }
  }
})
</script>

<style scoped>
/* Safe area handling for notched devices */
.safe-area-top {
  padding-top: env(safe-area-inset-top);
}

.safe-area-bottom {
  padding-bottom: env(safe-area-inset-bottom);
}

/* Ensure content doesn't overlap with bottom nav */
.v-main {
  padding-bottom: 56px; /* Height of bottom navigation */
}

/* Bottom navigation styling */
.v-bottom-navigation {
  box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.1);
}
</style>
